import React, { useState, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { prescriptionAPI } from "@/lib/prescription-api";
import { Loader2, ZoomIn, ZoomOut, X, ExternalLink } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { cn } from "@/lib/utils";

interface PrescriptionViewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  appointmentId: string;
  prescriptionUrl?: string | null;
  title?: string;
  parentType?: "prescription" | "meal_plan";
}

/**
 * Dialog for viewing prescriptions - either from a URL or by fetching HTML content
 * Similar to Flutter's implementation in individual_last_visit.dart
 */
export const PrescriptionViewDialog = ({
  open,
  onOpenChange,
  appointmentId,
  prescriptionUrl,
  title = "Prescription",
  parentType = "prescription",
}: PrescriptionViewDialogProps) => {
  const isNutrition = parentType === "meal_plan";
  const [scale, setScale] = useState(1);

  // Drag to scroll state
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [startY, setStartY] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [scrollTop, setScrollTop] = useState(0);

  // Fetch HTML content if no prescription URL is provided
  const {
    data: htmlContent,
    isLoading,
    isError,
  } = useQuery({
    queryKey: [
      isNutrition ? "nutrition-view" : "prescription-view",
      appointmentId,
    ],
    queryFn: () =>
      isNutrition
        ? prescriptionAPI.verifyNutritionReport(appointmentId)
        : prescriptionAPI.verifyPrescription(appointmentId),
    enabled: open && !prescriptionUrl,
    staleTime: 0,
  });

  // Reset state on close
  React.useEffect(() => {
    if (!open) {
      setScale(1);
    }
  }, [open]);

  const handleZoomIn = () => setScale((prev) => Math.min(prev + 0.1, 2));
  const handleZoomOut = () => setScale((prev) => Math.max(prev - 0.1, 0.5));

  // Drag Handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    if (!scrollContainerRef.current) return;
    setIsDragging(true);
    setStartX(e.pageX - scrollContainerRef.current.offsetLeft);
    setStartY(e.pageY - scrollContainerRef.current.offsetTop);
    setScrollLeft(scrollContainerRef.current.scrollLeft);
    setScrollTop(scrollContainerRef.current.scrollTop);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !scrollContainerRef.current) return;
    e.preventDefault();
    const x = e.pageX - scrollContainerRef.current.offsetLeft;
    const y = e.pageY - scrollContainerRef.current.offsetTop;
    const walkX = (x - startX) * 1.5;
    const walkY = (y - startY) * 1.5;
    scrollContainerRef.current.scrollLeft = scrollLeft - walkX;
    scrollContainerRef.current.scrollTop = scrollTop - walkY;
  };

  // If prescription URL is provided, open in new tab
  const handleOpenInNewTab = () => {
    if (prescriptionUrl) {
      window.open(prescriptionUrl, "_blank");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl h-[90vh] flex flex-col p-0 gap-0 [&>button.absolute]:hidden">
        <DialogHeader className="p-4 border-b flex flex-row items-center justify-between space-y-0 bg-white z-10">
          <DialogTitle>{title}</DialogTitle>
          <div className="flex items-center gap-2">
            {/* Zoom Controls */}
            <div className="flex items-center border rounded-md mr-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={handleZoomOut}
                disabled={scale <= 0.5}
                className="h-8 w-8"
              >
                <ZoomOut className="h-4 w-4" />
              </Button>
              <span className="w-12 text-center text-sm">
                {Math.round(scale * 100)}%
              </span>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleZoomIn}
                disabled={scale >= 2}
                className="h-8 w-8"
              >
                <ZoomIn className="h-4 w-4" />
              </Button>
            </div>

            {/* Open in New Tab Button (only if URL is available) */}
            {prescriptionUrl && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleOpenInNewTab}
                className="gap-2"
              >
                <ExternalLink className="h-4 w-4" />
                Open in New Tab
              </Button>
            )}

            {/* Close Button */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onOpenChange(false)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </DialogHeader>

        <div className="flex-1 bg-muted overflow-hidden relative">
          {prescriptionUrl ? (
            // If prescription URL is provided, show it in an iframe
            <div
              ref={scrollContainerRef}
              className={cn(
                "w-full h-full overflow-auto p-8 flex",
                isDragging ? "cursor-grabbing select-none" : "cursor-grab"
              )}
              onMouseDown={handleMouseDown}
              onMouseLeave={handleMouseLeave}
              onMouseUp={handleMouseUp}
              onMouseMove={handleMouseMove}
            >
              <div
                className="relative m-auto transition-all duration-200 ease-in-out"
                style={{
                  width: `${210 * scale}mm`,
                  height: `${297 * scale}mm`,
                }}
              >
                <div
                  style={{
                    transform: `scale(${scale})`,
                    transformOrigin: "top left",
                    width: "210mm",
                    height: "297mm",
                    transition: isDragging
                      ? "none"
                      : "transform 0.2s ease-in-out",
                  }}
                  className="bg-white shadow-lg pointer-events-none absolute top-0 left-0"
                >
                  <iframe
                    src={prescriptionUrl}
                    className="w-full h-full border-none pointer-events-none"
                    title="Prescription View"
                  />
                </div>
              </div>
            </div>
          ) : isLoading ? (
            <div className="absolute inset-0 flex items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : isError ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-destructive gap-2">
              <p>Failed to load prescription.</p>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Close
              </Button>
            </div>
          ) : (
            <div
              ref={scrollContainerRef}
              className={cn(
                "w-full h-full overflow-auto p-8 flex",
                isDragging ? "cursor-grabbing select-none" : "cursor-grab"
              )}
              onMouseDown={handleMouseDown}
              onMouseLeave={handleMouseLeave}
              onMouseUp={handleMouseUp}
              onMouseMove={handleMouseMove}
            >
              <div
                className="relative m-auto transition-all duration-200 ease-in-out"
                style={{
                  width: `${210 * scale}mm`,
                  height: `${297 * scale}mm`,
                }}
              >
                <div
                  style={{
                    transform: `scale(${scale})`,
                    transformOrigin: "top left",
                    width: "210mm",
                    height: "297mm",
                    transition: isDragging
                      ? "none"
                      : "transform 0.2s ease-in-out",
                  }}
                  className="bg-white shadow-lg pointer-events-none absolute top-0 left-0"
                >
                  <iframe
                    srcDoc={htmlContent}
                    className="w-full h-full border-none pointer-events-none"
                    title="Prescription View"
                    sandbox="allow-same-origin"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
